import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import branchesApi, { apiError } from "../../../../components/api/Branchesapi";

const API = import.meta.env.VITE_API_URL || "https://api.nexflowcrm.com/api/v1";
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
});

// Mirrors the backend's utils/role.js exactly — must stay in sync.
const toRoleSlug = (positionName: string) => positionName.trim().toLowerCase().replace(/\s+/g, "_");

type Position = {
  id: string;
  name: string;
  level: number;
  reportsTo: string | null;
  color: string;
  dataAccess: string;
  permissions: Record<string, { view: boolean; create: boolean; edit: boolean; delete_: boolean }>;
  userCount: number;
  createdAt: string;
};

type BranchOption = { id: string; name: string; code?: string };

// FIX: added `team` — without it, this modal couldn't read a manager's
// real assigned team, so auto-team-assign silently diverged from the
// logic used in TeamsPage.tsx (which does have this field).
type CompanyUser = {
  id: string;
  name: string;
  role: string;
  branch: string | null;
  team: string | null;
  isActive: boolean;
};

type Status = "Active" | "Inactive";

type UserData = {
  name: string; email: string; phone: string; password?: string;
  role: string; branch: string; branchId: string; status: Status; isActive: boolean;
  managerId?: string | null; team?: string;
  companyId?: string; lastLogin?: string; createdAt?: string;
};

type Props = {
  user: (UserData & { id: string }) | null;
  isSaving?: boolean;
  onClose: () => void;
  onSave: (data: Omit<UserData, "isActive" | "companyId" | "lastLogin" | "createdAt">, id?: string) => void;
};

export default function UserModal({ user, isSaving = false, onClose, onSave, }: Props) {
  const isEdit = !!user;
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [positionId, setPositionId] = useState("");
  const [branchId, setBranchId] = useState(user?.branchId || "");
  const [branchName, setBranchName] = useState(user?.branch || "");
  const [status, setStatus] = useState<Status>(user?.status || "Active");
  const [password, setPassword] = useState("");
  const [managerId, setManagerId] = useState(user?.managerId || "");
  const [sendCredentials, setSendCredentials] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [positionsList, setPositionsList] = useState<Position[]>([]);
  const [branchList, setBranchList] = useState<BranchOption[]>([]);
  const [allUsers, setAllUsers] = useState<CompanyUser[]>([]);

  const selectedPosition = positionsList.find((p) => p.id === positionId);

  // ─── Fetch positions ─────────────────────────────────────
  const fetchPositions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/positions`, { headers: authHeader() });
      setPositionsList(res.data?.data?.items || []);
    } catch (err: any) {
      console.error(err.response?.data?.message || "Failed to load positions");
    }
  }, []);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  // ─── Fetch branches ──────────────────────────────────────
  const fetchBranches = useCallback(async () => {
    try {
      const { items } = await branchesApi.list();
      setBranchList(items);
    } catch (err) {
      console.error(apiError(err, "Couldn't load branches"));
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  // ─── Fetch existing users, to compute eligible managers ───
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/company-user/all`, { headers: authHeader() });
      setAllUsers(res.data?.data?.items || []);
    } catch (err: any) {
      console.error(err.response?.data?.message || "Failed to load users for manager selection");
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Once positions load, preselect the editing user's current position by
  // matching their stored role slug against each position's slugified name.
  useEffect(() => {
    if (!user || positionsList.length === 0 || positionId) return;
    const match = positionsList.find((p) => toRoleSlug(p.name) === user.role);
    setPositionId(match?.id || positionsList[0]?.id || "");
  }, [user, positionsList, positionId]);

  // For a brand-new user, default to the first available position once loaded
  useEffect(() => {
    if (user || positionId || positionsList.length === 0) return;
    setPositionId(positionsList[0].id);
  }, [user, positionId, positionsList]);

  // ─── Reports To / manager eligibility ──────────────────────
  // A position's `reportsTo` stores the parent position's NAME, so we look
  // up that position, then find active users whose role matches its slug.
  const parentPosition = useMemo(
    () => positionsList.find((p) => p.name === selectedPosition?.reportsTo) || null,
    [positionsList, selectedPosition]
  );

  const eligibleManagers = useMemo(() => {
    if (!parentPosition) return [];
    const parentSlug = toRoleSlug(parentPosition.name);
    return allUsers.filter(
      (u) => u.isActive && u.role === parentSlug && u.id !== user?.id
    );
  }, [allUsers, parentPosition, user?.id]);

  const autoSelectedManager = eligibleManagers.length === 1 ? eligibleManagers[0] : null;

  // Auto-select the manager when there's exactly one eligible option
  useEffect(() => {
    if (autoSelectedManager) {
      setManagerId(autoSelectedManager.id);
    } else if (selectedPosition && selectedPosition.level === 1) {
      setManagerId(""); // top-level position — no manager
    }
  }, [autoSelectedManager, selectedPosition]);

  // ── Team auto-assignment ──────────────────────────────────
  // FIX: now mirrors TeamsPage.tsx's precedence exactly —
  //   1. manager's own stored `team`, if set
  //   2. "Leadership" when the manager reports directly to a top-level position
  //   3. "{manager's branch} Sales" as a last resort
  const activeManager = eligibleManagers.find((m) => m.id === managerId) || autoSelectedManager;
  const autoTeamName = useMemo(() => {
    if (!selectedPosition || selectedPosition.level === 1) return "";
    if (activeManager?.team) return activeManager.team;
    if (parentPosition?.level === 1) return "Leadership";
    if (activeManager?.branch.name) return `${activeManager.branch.name} Sales`;
    return "";
  }, [selectedPosition, parentPosition, activeManager]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = "Name is required";
    if (!email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = "Invalid email format";
    if (!phone.trim()) e.phone = "Phone is required";
    if (!isEdit && !password.trim()) e.password = "Password is required";
    if (password && password.length < 5) e.password = "Min 5 characters";
    if (!positionId || !selectedPosition) e.position = "Position is required";
    if (selectedPosition && selectedPosition.level > 1 && !managerId) {
      e.manager = "A direct manager is required for this position";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate() || isSaving) return;
    onSave(
      {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password: password || undefined, // FIX: don't invent a fake fallback — empty/undefined means "keep current" on edit
        role: toRoleSlug(selectedPosition!.name),
        branch: branchName,
        branchId,
        status,
        managerId: managerId || null,
        team: autoTeamName,
      },
      user?.id
    );
  };

  const handleBranchChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setBranchId(id);
    setBranchName(branchList.find((b) => b.id === id)?.name || "");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-lg animate-slide-in max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? "Edit User" : "Add New User"}
          </h2>
          <button onClick={onClose} disabled={isSaving}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. John Doe"
              className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all ${errors.name ? "border-red-400" : "border-gray-200"}`}
            />
            {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. john@company.com"
              className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all ${errors.email ? "border-red-400" : "border-gray-200"}`}
            />
            {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. +91 98201 23456"
              className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all ${errors.phone ? "border-red-400" : "border-gray-200"}`}
            />
            {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {isEdit ? "Password (leave blank to keep current)" : "Password"}
              {!isEdit && <span className="text-red-500"> *</span>}
            </label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={isEdit ? "Leave blank to keep current" : "Min. 5 characters"}
              className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all ${errors.password ? "border-red-400" : "border-gray-200"}`}
            />
            {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
          </div>

          {/* Position */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Position <span className="text-red-500">*</span>
            </label>
            <select
              value={positionId}
              onChange={(e) => { setPositionId(e.target.value); setManagerId(""); }}
              disabled={positionsList.length === 0}
              className={`w-full px-3.5 py-2.5 rounded-lg border bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all cursor-pointer disabled:bg-gray-50 disabled:cursor-not-allowed ${errors.position ? "border-red-400" : "border-gray-200"}`}
            >
              <option value="" disabled>
                {positionsList.length === 0 ? "No positions available" : "Select position…"}
              </option>
              {[...positionsList].sort((a, b) => a.level - b.level).map((p) => (
                <option key={p.id} value={p.id}>{p.name} (Level {p.level})</option>
              ))}
            </select>
            {errors.position && <p className="text-xs text-red-600 mt-1">{errors.position}</p>}
            {selectedPosition && (
              <p className="text-xs text-gray-400 mt-1">
                Role: {toRoleSlug(selectedPosition.name)}
                <button type="button" onClick={() => navigate("/settings/positions")}
                  className="ml-1 text-indigo-600 hover:underline">
                  Manage Positions &rarr;
                </button>
              </p>
            )}
          </div>

          {/* Reports To (Direct Manager) */}
          {selectedPosition && selectedPosition.level === 1 ? (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">
                &#x1F451; Top level position &mdash; reports to no one
              </p>
            </div>
          ) : selectedPosition && eligibleManagers.length === 0 ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reports To (Direct Manager) <span className="text-red-500">*</span>
              </label>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  &#x26A0; No {parentPosition?.name || "manager"} found. Add a {parentPosition?.name || "manager"} first.
                </p>
              </div>
              {errors.manager && <p className="text-xs text-red-600 mt-1">{errors.manager}</p>}
            </div>
          ) : selectedPosition && eligibleManagers.length > 0 ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reports To (Direct Manager) <span className="text-red-500">*</span>
              </label>
              {autoSelectedManager ? (
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <p className="text-xs text-green-600">
                      &#x2713; Auto-selected: {autoSelectedManager.name} (only {parentPosition?.name})
                    </p>
                  </div>
                  <input
                    type="text"
                    value={`${autoSelectedManager.name} | ${parentPosition?.name} | ${autoSelectedManager.branch.name || "—"}`}
                    readOnly
                    className="w-full px-3.5 py-2.5 rounded-lg border border-green-300 bg-green-50 text-sm text-gray-700"
                  />
                </div>
              ) : (
                <select
                  value={managerId}
                  onChange={(e) => setManagerId(e.target.value)}
                  className={`w-full px-3.5 py-2.5 rounded-lg border bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all ${errors.manager ? "border-red-400" : "border-gray-200"}`}
                >
                  <option value="">Select manager...</option>
                  {eligibleManagers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} | {parentPosition?.name} | {m.branch.name || "—"}
                    </option>
                  ))}
                </select>
              )}
              {errors.manager && <p className="text-xs text-red-600 mt-1">{errors.manager}</p>}
            </div>
          ) : null}

          {/* Team auto-assign */}
          {selectedPosition && selectedPosition.level > 1 && autoTeamName && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Team</label>
              <input
                type="text"
                value={autoTeamName}
                readOnly
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-700 cursor-default"
              />
              <p className="text-[11px] text-gray-400 mt-1">Auto-assigned based on manager</p>
            </div>
          )}

          {/* Branch */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Branch</label>
            <select
              value={branchId}
              onChange={handleBranchChange}
              disabled={branchList.length === 0}
              className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all cursor-pointer disabled:bg-gray-50 disabled:cursor-not-allowed"
            >
              <option value="">
                {branchList.length === 0 ? "No branches available" : "Select branch…"}
              </option>

              {branchId && !branchList.some((b) => b.id === branchId) && (
                <option value={branchId}>{branchName || "Current branch"} (inactive)</option>
              )}

              {branchList.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code ? `${b.name} (${b.code})` : b.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as Status)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all cursor-pointer">
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {/* Send credentials */}
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={sendCredentials}
              onChange={(e) => setSendCredentials(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Send login credentials via email
          </label>

          {/* Actions */}
          <div className="flex items-center gap-2.5 pt-2 justify-end">
            <button type="button" onClick={onClose} disabled={isSaving}
              className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={isSaving}
              className="px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white transition-colors whitespace-nowrap disabled:opacity-60 flex items-center gap-2">
              {isSaving ? (
                <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{isEdit ? "Updating..." : "Creating..."}</>
              ) : (
                isEdit ? "Update User" : "Add Member"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}